// src/components/common/InputField.tsx
import React, {useRef, useState, useEffect, useCallback} from 'react';
import '../../styles/components/InputField.css';

interface AttachedFile {
  file: File;
  id: string;
  type: 'image' | 'document';
  extractedText?: string;
}

interface InputFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  onSubmit: (attachedFiles?: AttachedFile[]) => void;
  loading?: boolean;
  onFileProcess?: (files: AttachedFile[]) => void;
}

const InputField: React.FC<InputFieldProps> = ({
  value,
  onChange,
  placeholder = "Type your message...",
  disabled = false,
  onSubmit,
  loading = false,
  onFileProcess
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [processingFiles, setProcessingFiles] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  
  // ===== FIXED: Use useRef for recognition instance =====
  const recognitionRef = useRef<any>(null);
  const finalTranscriptRef = useRef<string>('');

  // ===== FIXED: Initialize speech recognition only once =====
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      
      if (SpeechRecognition && !recognitionRef.current) {
        const recognitionInstance = new SpeechRecognition();
        recognitionInstance.continuous = true;
        recognitionInstance.interimResults = true;
        recognitionInstance.lang = 'en-US';
        recognitionInstance.maxAlternatives = 3;

        recognitionInstance.onstart = () => {
          setIsListening(true);
          setInterimTranscript('');
          finalTranscriptRef.current = '';
        };

        recognitionInstance.onresult = (event: any) => {
          let interim = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscriptRef.current += transcript + ' ';
              onChange(value + finalTranscriptRef.current);
            } else {
              interim += transcript;
            }
          }
          
          setInterimTranscript(interim);
        };

        recognitionInstance.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
          setInterimTranscript('');
          finalTranscriptRef.current = '';
          
          if (event.error === 'not-allowed') {
            alert('Microphone access denied. Please allow microphone permissions to use speech-to-text.');
          } else if (event.error === 'audio-capture') {
            alert('No microphone found. Please ensure a microphone is connected and try again.');
          }
        };

        recognitionInstance.onend = () => {
          setIsListening(false);
          setInterimTranscript('');
          finalTranscriptRef.current = '';
        };

        recognitionRef.current = recognitionInstance;
      }
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [onChange, value]); // ===== FIXED: Removed recognition from deps =====

  // ===== FIXED: Auto-resize textarea with proper cleanup =====
  useEffect(() => {
    if (textareaRef.current) {
      const timeoutId = setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
          textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 60) + 'px';
        }
      }, 10);
      
      return () => clearTimeout(timeoutId);
    }
  }, [value, interimTranscript]);

  // ===== FIXED: Memoize toggle function =====
  const toggleSpeechToText = useCallback(() => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    try {
      if (isListening) {
        recognitionRef.current.stop();
        setIsListening(false);
        setInterimTranscript('');
        finalTranscriptRef.current = '';
      } else {
        // Reset transcript before starting
        finalTranscriptRef.current = '';
        setInterimTranscript('');
        setTimeout(() => {
          if (recognitionRef.current) {
            recognitionRef.current.start();
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error toggling speech recognition:', error);
      setIsListening(false);
      setInterimTranscript('');
      finalTranscriptRef.current = '';
    }
  }, [isListening]);

  const extractTextFromImage = async (file: File): Promise<string> => {
    try {
      if (typeof window !== 'undefined' && (window as any).Tesseract) {
        const Tesseract = (window as any).Tesseract;
        const result = await Tesseract.recognize(file);
        return result.data.text || `[No text found in ${file.name}]`;
      } else {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = async () => {
            setTimeout(() => {
              resolve(`[Simulated extracted text from ${file.name}]`);
            }, 1000);
          };
          reader.readAsDataURL(file);
        });
      }
    } catch (error) {
      console.error('Error extracting text from image:', error);
      return `[Error processing image: ${file.name}]`;
    }
  };

  const extractTextFromDocument = async (file: File): Promise<string> => {
    const fileType = file.type;
    
    try {
      if (fileType === 'text/plain' || fileType.includes('text/')) {
        return await file.text();
      } else if (fileType === 'application/json') {
        const text = await file.text();
        return JSON.stringify(JSON.parse(text), null, 2);
      } else if (fileType === 'application/pdf') {
        if (typeof window !== 'undefined' && (window as any).pdfjsLib) {
          const pdfjsLib = (window as any).pdfjsLib;
          const arrayBuffer = await file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
          let fullText = '';
          
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map((item: any) => item.str).join(' ');
            fullText += pageText + '\n';
          }
          
          return fullText || `[No extractable text from PDF: ${file.name}]`;
        }
        return `[PDF content from ${file.name}]`;
      }
      
      return `[Content from ${file.name} - ${fileType}]`;
    } catch (error) {
      console.error('Error reading document:', error);
      return `[Error reading ${file.name}]`;
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setProcessingFiles(true);
    const newAttachedFiles: AttachedFile[] = [];

    for (const file of files) {
      const isImage = file.type.startsWith('image/');
      const attachedFile: AttachedFile = {
        file,
        id: `${Date.now()}-${Math.random()}`,
        type: isImage ? 'image' : 'document'
      };

      try {
        if (isImage) {
          attachedFile.extractedText = await extractTextFromImage(file);
        } else {
          attachedFile.extractedText = await extractTextFromDocument(file);
        }
      } catch (error) {
        console.error(`Error processing ${file.name}:`, error);
        attachedFile.extractedText = `[Error processing ${file.name}]`;
      }

      newAttachedFiles.push(attachedFile);
    }

    setAttachedFiles((prev) => [...prev, ...newAttachedFiles]);
    setProcessingFiles(false);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (onFileProcess) {
      onFileProcess([...attachedFiles, ...newAttachedFiles]);
    }
  };

  const removeAttachedFile = (id: string) => {
    setAttachedFiles((prev) => prev.filter((file) => file.id !== id));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if ((value.trim() || attachedFiles.length > 0) && !disabled && !isListening) {
      onSubmit(attachedFiles.length > 0 ? attachedFiles : undefined);
      onChange('');
      setAttachedFiles([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const displayValue = value + (interimTranscript ? interimTranscript : '');

  return (
    <div className="input-field-container">
      {/* Attached files preview */}
      {attachedFiles.length > 0 && (
        <div className="attached-files">
          {attachedFiles.map((attachedFile) => (
            <div key={attachedFile.id} className="attached-file">
              <span className="file-info">
                {attachedFile.type === 'image' ? '🖼️' : '📄'} 
                {attachedFile.file.name}
                {attachedFile.extractedText && (
                  <span className="extracted-text-preview">
                    ({attachedFile.extractedText.substring(0, 50)}...)
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => removeAttachedFile(attachedFile.id)}
                className="remove-file-button"
                title="Remove file"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Speech recognition indicator */}
      {isListening && (
        <div className="speech-indicator">
          <div className="speech-pulse"></div>
          <span className="speech-text">
            Listening... {interimTranscript && `"${interimTranscript}"`}
            <button 
              type="button" 
              onClick={toggleSpeechToText}
              className="stop-recording-button"
            >
              Stop
            </button>
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="input-field-form">
        <div className="input-wrapper">
          <div className="input-main-container">
            <textarea
              ref={textareaRef}
              value={displayValue}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled || isListening}
              className="input-textarea"
              rows={1}
            />
            
            <div className="input-controls">
              <button
                type="button"
                onClick={handleAttachmentClick}
                disabled={disabled || processingFiles || isListening}
                className="control-button attachment-button"
                title="Attach file"
              >
                {processingFiles ? (
                  <span className="processing-indicator">⏳</span>
                ) : (
                  <span className="attachment-icon">📎</span>
                )}
              </button>

              <button
                type="button"
                onClick={toggleSpeechToText}
                disabled={disabled || !recognitionRef.current}
                className={`control-button speech-button ${isListening ? 'listening' : ''}`}
                title={isListening ? 'Stop listening' : 'Start speech to text'}
              >
                {isListening ? (
                  <span className="recording-indicator">🔴</span>
                ) : (
                  <span className="mic-icon">🎤</span>
                )}
              </button>

              <button 
                type="submit" 
                disabled={disabled || (!value.trim() && attachedFiles.length === 0) || loading || isListening}
                className="submit-button control-button"
              >
                {loading ? (
                  <span className="loading-indicator">
                    <span className="spinner"></span>
                  </span>
                ) : (
                  <span className="send-icon">🚀</span>
                )}
              </button>
            </div>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          multiple
          accept="image/*,.txt,.json,.csv,.md,.doc,.docx,.pdf"
          style={{display: 'none'}}
        />
      </form>
    </div>
  );
};

export default InputField;